import styled from 'styled-components';

import {
    BACKGROUND_DEEP,
    CONTENT_BASE,
    PRIMARY_BASE,
    PRIMARY_DISABLED,
    PRIMARY_TEXT_LINK_HOVER,
    PRIMARY_TEXT_LINK_PRESS,
    TERTIARY_BORDER,
} from '@amzn/ring-common-style-library/lib/colors';
import {
    BODY_LARGE_MEDIUM,
    BODY_NORMAL_MEDIUM,
    BODY_SMALL_LIGHT,
    BODY_SMALL_MEDIUM,
    HEADING_NORMAL_MEDIUM,
    HEADING_SMALL_LIGHT,
    HEADING_SMALL_MEDIUM,
    TEXTLINK_SMALL_MEDIUM,
} from '@amzn/ring-common-style-library/lib/typography';
import { MessageParticipantRole } from '@amzn/ring-cs-chatbot-service-model';

const customNodeStyling = (nodeClass: string) => `
    .${nodeClass}:first-child {
        background-color: ${BACKGROUND_DEEP};
        margin: -19px -17px;
        padding: 8px 0px 0px 0px;
    }

    .${nodeClass}:not(:first-child) {
        padding-top: 0px;
    }
`;

const Bubble = styled.div<{
    badge?: MessageParticipantRole;
    customer?: boolean;
}>`
    display: inline-flex;
    flex-direction: column;
    flex-wrap: wrap;
    max-width: 300px;
    position: relative;

    background-color: ${({ customer }) => (customer ? TERTIARY_BORDER : PRIMARY_DISABLED)};
    border-radius: ${({ customer }) => (customer ? '8px 0px 8px 8px;' : '0px 8px 8px 8px')};
    padding: 16px;

    ${BODY_SMALL_LIGHT}

    color: ${CONTENT_BASE};

    p {
        margin: 0;
    }
`;

const Badge = styled.div`

`;

const Content = styled.div`
    /* Preserve newlines for textOnly mode */
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: break-word;
    
    .tiptap {
        /* Custom Select Styling (Remove Bubble when only element) */
        ${customNodeStyling('node-feedback')}
        ${customNodeStyling('node-multiselect')}
        ${customNodeStyling('node-product')}
        ${customNodeStyling('node-select')}
        ${customNodeStyling('node-rating')}
        
        /* When Select Component is NOT only element remove indentation */
        .node-select:not(:first-child) {
            padding-top: 12px;
            margin-left: -7px;

            .select-indentation {
                display: none;
            }
        }

        .node-multiselect:not(:first-child) {
            padding-top: 12px;

            .multiselect-items {
                border-left: none;
                margin-left: -7px;
            }
        }

        a {
            ${TEXTLINK_SMALL_MEDIUM};
            color: ${CONTENT_BASE} !important;
            padding: 4px 0;
        }

        h1 {
            ${HEADING_NORMAL_MEDIUM}
            padding: 0px;
            margin: 0px;
        }

        h2 {
            ${HEADING_SMALL_MEDIUM}
            padding: 0px;
            margin: 0px;
        }

        h3 {
            ${HEADING_SMALL_LIGHT}
            padding: 0px;
            margin: 0px;
        }

        h4 {
            ${BODY_LARGE_MEDIUM}
            padding: 0px;
            margin: 0px;
        }

        h5 {
            ${BODY_NORMAL_MEDIUM}
            padding: 0px;
            margin: 0px;
        }

        h6 {
            ${BODY_SMALL_MEDIUM}
            padding: 0px;
            margin: 0px;
        }

        ul,
        ol {
            padding-inline-start: 15px;
        }
        word-break: break-word; /* Force words to break */
        white-space: pre-wrap; /* Ensure text wraps normally */
        overflow-wrap: break-word; /* Wrap unbroken words (urls) */
    }
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid ${CONTENT_BASE};
    margin: 12px 0px 8px;
`;

const ReceiptIndicator = styled.div<{
    receipt?: 'READ' | 'DELIVERED';
}>`
    display: flex;
    align-items: center;

    height: 16px;
    width: 16px;

    position: absolute;
    bottom: 5px;
    right: 5px;

    opacity: ${({ receipt }) => (receipt === 'READ' ? 1 : 0.5)};

    svg path {
        fill: ${CONTENT_BASE};
    }

`;

const Sources = styled.div`
    a {
        ${TEXTLINK_SMALL_MEDIUM};
        color: ${PRIMARY_TEXT_LINK_PRESS};
        text-decoration: none;
        padding: 2px 0px;

        &:hover {
            color: ${PRIMARY_TEXT_LINK_HOVER};
        }
        
        &:active {
            color: ${CONTENT_BASE};
        }

        &:visited {
            color: ${PRIMARY_BASE};
        }
    }
    ul {
        margin: 0;
        padding-left: 12px;
        list-style-type: circle;
    }
    li {
        margin-bottom: 4px;
    }
`;

const SourceTitle = styled.div`
    ${BODY_NORMAL_MEDIUM};
    margin: 0 0 8px 0;
`;

const Events = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const EventCard = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
    padding: 8px;
    background-color: ${BACKGROUND_DEEP};
    border-radius: 6px;
`;

const EventDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0; /* Allow text to wrap */
`;

const Wrapper = styled.div<{
    customer?: boolean;
}>`
    display: flex;
    flex-direction: ${({ customer }) => (customer ? 'row-reverse' : 'row')};
    width: 100%;
    gap: 8px;
`;

export default {
    Badge,
    Bubble,
    Content,
    Divider,
    EventCard,
    EventDetails,
    Events,
    ReceiptIndicator,
    Sources,
    SourceTitle,
    Wrapper
}
